import { useState, useEffect, useCallback, useRef } from 'react';
import { throttle } from 'lodash-es';
import type { NotificationListRes, NotificationItem } from '../services';

type fetchDataFunc = (page: number, pageSize: number) => Promise<NotificationListRes>;

export function useInfiniteScroll(fetchFn: fetchDataFunc, pageSize: number = 10) {
  const [items, setItems] = useState<Array<NotificationItem>>([]);   // 已加载的数据
  const [page, setPage] = useState(1);          // 当前页码, 从1开始
  const [loading, setLoading] = useState(false); // 是否正在加载
  const [hasMore, setHasMore] = useState(true);  // 是否还有更多数据
  const containerRef = useRef<HTMLDivElement>(null); // 滚动容器的 ref

  const loadMore = useCallback(async (currentPage: number) => {
    if (loading || !hasMore) return;

    setLoading(true);
    try {
      const { list } = await fetchFn(currentPage, pageSize);
      if (list && list.length) {
        setItems((prev) => {
          if (currentPage > 1) {
            return [...prev, ...list];
          }
          return [...list];
        });
        setPage(currentPage + 1);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error('load data failed:', error);
    } finally {
      setLoading(false);
    }
  }, [loading, hasMore, fetchFn, pageSize]);

  // 🧠 滚动事件处理
  const handleScroll = useCallback(
    throttle(() => {
      const container = containerRef.current;
      if (!container) return;
      const scrollTop = container.scrollTop;
      const clientHeight = container.clientHeight;
      const scrollHeight = container.scrollHeight;
      // 距离底部 100px 时触发加载
      if (scrollTop + clientHeight >= scrollHeight - 100) {
        loadMore(page);
      }
    }, 200),
    [page, loadMore]
  );

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [containerRef, handleScroll]);


  useEffect(() => {
    loadMore(1); // 初始加载第一页
  }, []);

  return {
    items,
    setItems,
    loading,
    hasMore,
    containerRef
  };
}